import React, { memo, ReactNode, useState } from 'react';
import { TabularHeader } from './components/TabularHeader';
import { TabularSwitcher } from './components/TabularSwitcher';
import { TabularContent } from './components/TabularContent';
import { TabularBreadcrumb } from './components/TabularBreadcrumb';


export interface TabularLayoutProps {
  // Page metadata
  title?: string;
  subtitle?: string;
  
  // Breadcrumb configuration
  breadcrumbItems?: BreadcrumbLink[];
  
  // Tab configuration
  hasTabs?: boolean;
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  
  // Content
  children: ReactNode;
  
  // Layout options
  maxWidth?: string | 'none'; // Set to 'none' or undefined for unlimited width, defaults to '1400px'
  className?: string;
  
  // Allow composition with individual components
  components?: {
    header?: ReactNode;
    breadcrumb?: ReactNode;
    navigation?: ReactNode;
    content?: ReactNode;
  };
}

export interface BreadcrumbLink {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface TabConfig {
  id: string;
  label: string;
  disabled?: boolean;
  content?: ReactNode;
}

export const TabularLayout = memo(function TabularLayout({ 
  title,
  subtitle,
  breadcrumbItems,
  hasTabs = false,
  tabs,
  activeTab: propsActiveTab,
  onTabChange: propsOnTabChange,
  children,
  maxWidth = '1400px',
  className = '',
  components
}: TabularLayoutProps) {
  // Internal state for tabs if not controlled
  const [defaultActiveTab, setDefaultActiveTab] = useState(tabs?.[0]?.id || 'tab1');
  
  // Use provided tabs and handlers, or fall back to defaults
  const actualActiveTab = propsActiveTab || defaultActiveTab;
  const actualOnTabChange = propsOnTabChange || setDefaultActiveTab;
  
  // Determine effective max width
  const effectiveMaxWidth = maxWidth === 'none' ? undefined : maxWidth;
  
  // If components are provided, use composition mode
  if (components) {
    return (
      <div className={`tabular-layout ${className}`}>
        <div className="tabular-layout-inner" style={{ maxWidth: effectiveMaxWidth }}>
          {components.breadcrumb}
          {components.header}
          {components.navigation}
          {components.content || children}
        </div>
      </div>
    );
  }
  
  // Otherwise, use automatic mode with props
  return (
    <div className={`tabular-layout ${className}`}>
      <div className="tabular-layout-inner" style={{ maxWidth: effectiveMaxWidth }}>
        {/* Breadcrumb */}
        {breadcrumbItems && breadcrumbItems.length > 0 && (
          <div className="tabular-layout-breadcrumb">
            <TabularBreadcrumb items={breadcrumbItems} />
          </div>
        )}

        {/* Page header */}
        {(title || subtitle) && (
          <div className={`tabular-layout-header ${hasTabs ? '' : 'no-tabs'} ${subtitle ? 'has-subtitle' : ''}`}>
            <TabularHeader 
              title={title || ''} 
              subtitle={subtitle}
            />
          </div>
        )}

        {/* Tabs or divider */}
        {hasTabs && tabs ? (
          <div className="tabular-layout-nav">
            <TabularSwitcher
              tabs={tabs}
              activeTab={actualActiveTab}
              onTabChange={actualOnTabChange}
            />
          </div>
        ) : (
          <div className="tabular-layout-divider" />
        )}

        {/* Page content */}
        <div className="tabular-layout-content">
          {children}
        </div>
      </div>
    </div>
  );
});
